import { useState } from 'react';
import styled from 'styled-components';
import { useAnnotateEconAnomalyMutation, useCreateRequestMutation } from '@shared/api';
import type { EconAnomaly } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  decisionLabel,
  decisionTone,
  fmtNumber,
  fmtPercent,
  labelAnomalyKind,
  severityLabel,
  severityTone,
  useEconPermission,
} from '@entities/economic/types';
import { alertPulse } from './econAnimations';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const AnomalyCard = styled(Card)<{ $critical: boolean }>`
  padding: ${({ theme }) => theme.spacing(4)};
  border-color: ${({ theme, $critical }) =>
    $critical ? `${theme.colors.status.danger}55` : theme.colors.border};
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Вид аномалии (русская подпись `kind`) — техническая категория над
 *  человекочитаемым заголовком. */
const Kind = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const Desc = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Badges = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-shrink: 0;
`;

/** Бейдж критичной неразмеченной аномалии — мягкий пульс, чтобы ревьюер
 *  не пропустил. На reduce-motion глобальная глушилка снимает анимацию. */
const PulseBadge = styled(Badge)`
  animation: ${alertPulse} 1800ms ${({ theme }) => theme.motion.easeInOut} infinite;
`;

const Numbers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const NumItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const NumLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NumValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const CrossModuleBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(2.5)};
  padding-top: ${({ theme }) => theme.spacing(2.5)};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  flex-wrap: wrap;
`;

const CrossModuleLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: 4px;
`;

const CommentBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const CommentInput = styled.textarea`
  flex: 1;
  min-width: 240px;
  min-height: 64px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const DecisionNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

type Decision = 'accepted' | 'rejected' | 'disputed';

function AnomalyItem({
  anomaly,
  runId,
  objectScope,
}: {
  anomaly: EconAnomaly;
  runId: string;
  objectScope?: string | null;
}) {
  const { can_write } = useEconPermission();
  const [annotate, { isLoading }] = useAnnotateEconAnomalyMutation();
  const [createRequest, { isLoading: creatingReq }] = useCreateRequestMutation();
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [requestNotice, setRequestNotice] = useState<string | null>(null);

  const decided = Boolean(anomaly.decision);
  const isCritical = anomaly.severity === 'critical';

  async function decide(decision: Decision, withComment?: string) {
    try {
      await annotate({
        id: anomaly.id,
        runId,
        decision,
        comment: withComment?.trim() || undefined,
      }).unwrap();
      setDisputeOpen(false);
      setRejectOpen(false);
      setComment('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('annotateEconAnomaly failed', err);
    }
  }

  async function dispatchCrossModuleRequest(
    requestType: 'contract_price_revision' | 'legal_claim' | 'geodesy',
    actionLabel: string,
  ) {
    setRequestNotice(null);
    const summaryText = `${anomaly.title}${anomaly.description ? ` — ${anomaly.description}` : ''}`;
    try {
      await createRequest({
        request_type: requestType,
        source_function: 'econ',
        title: `[Экономист] ${anomaly.title}`,
        priority: isCritical ? 'high' : 'normal',
        submit: true,
        payload: {
          description: summaryText,
          comment: summaryText,
          anomaly_id: anomaly.id,
          anomaly_kind: anomaly.kind,
          run_id: runId,
          object_scope: objectScope ?? null,
          expected: anomaly.expected,
          actual: anomaly.actual,
          delta: anomaly.delta,
        },
      }).unwrap();
      setRequestNotice(`Отправлена заявка: «${actionLabel}»`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('createRequest from anomaly failed', err);
      setRequestNotice('Не удалось отправить заявку в смежный модуль.');
    }
  }

  return (
    <AnomalyCard $critical={isCritical && !decided}>
      <Head>
        <div>
          {anomaly.kind && <Kind>{labelAnomalyKind(anomaly.kind)}</Kind>}
          <Title>{anomaly.title}</Title>
          {anomaly.description && <Desc>{anomaly.description}</Desc>}
        </div>
        <Badges>
          {isCritical && !decided ? (
            <PulseBadge $tone={severityTone(anomaly.severity)}>
              {severityLabel(anomaly.severity)}
            </PulseBadge>
          ) : (
            <Badge $tone={severityTone(anomaly.severity)}>{severityLabel(anomaly.severity)}</Badge>
          )}
        </Badges>
      </Head>

      <Numbers>
        <NumItem>
          <NumLabel>Ожидалось</NumLabel>
          <NumValue>{fmtNumber(anomaly.expected)}</NumValue>
        </NumItem>
        <NumItem>
          <NumLabel>Фактически</NumLabel>
          <NumValue>{fmtNumber(anomaly.actual)}</NumValue>
        </NumItem>
        <NumItem>
          <NumLabel>Расхождение</NumLabel>
          <NumValue>{fmtNumber(anomaly.delta)}</NumValue>
        </NumItem>
        <NumItem>
          <NumLabel>Доля</NumLabel>
          <NumValue>{fmtPercent(anomaly.delta_pct)}</NumValue>
        </NumItem>
      </Numbers>

      {decided ? (
        <>
          <Actions>
            <Badge $tone={decisionTone(anomaly.decision ?? '')}>
              {decisionLabel(anomaly.decision ?? '')}
            </Badge>
          </Actions>
          {anomaly.decision_comment && (
            <DecisionNote>Комментарий: {anomaly.decision_comment}</DecisionNote>
          )}
        </>
      ) : (
        <>
          <Actions>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={isLoading || !can_write}
              title={!can_write ? 'Только просмотр' : undefined}
              onClick={() => decide('accepted')}
            >
              Принять
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={isLoading || !can_write}
              title={!can_write ? 'Только просмотр' : undefined}
              onClick={() => {
                setRejectOpen(false);
                setDisputeOpen((v) => !v);
              }}
            >
              Спорно
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={isLoading || !can_write}
              title={!can_write ? 'Только просмотр' : undefined}
              onClick={() => {
                if (isCritical) {
                  setDisputeOpen(false);
                  setRejectOpen((v) => !v);
                } else {
                  decide('rejected');
                }
              }}
            >
              Отклонить
            </Button>
          </Actions>
          {disputeOpen && (
            <CommentBox>
              <CommentInput
                placeholder="Почему отклонение спорное — контекст для агента…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                aria-label="Комментарий к спорной аномалии"
              />
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                disabled={isLoading || !can_write}
                onClick={() => decide('disputed', comment)}
              >
                Пометить спорной
              </Button>
            </CommentBox>
          )}
          {rejectOpen && (
            <CommentBox>
              <CommentInput
                placeholder="Обоснование отклонения критической аномалии (сохраняется в память прецедентов ПЭО)…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                aria-label="Обоснование отклонения критической аномалии"
              />
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                disabled={isLoading || !can_write || !comment.trim()}
                onClick={() => decide('rejected', comment)}
              >
                Подтвердить отклонение
              </Button>
            </CommentBox>
          )}
        </>
      )}

      {can_write && (
        <CrossModuleBar>
          <CrossModuleLabel>Сквозные действия:</CrossModuleLabel>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={creatingReq}
            onClick={() =>
              dispatchCrossModuleRequest('contract_price_revision', 'Инициация ДС в Юрслужбу')
            }
          >
            Инициировать ДС в Юрслужбу
          </Button>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={creatingReq}
            onClick={() => dispatchCrossModuleRequest('legal_claim', 'Подготовка претензии')}
          >
            Претензия в Юрслужбу
          </Button>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={creatingReq}
            onClick={() => dispatchCrossModuleRequest('geodesy', 'Контрольный замер (Геодезия)')}
          >
            Запросить замер в Геодезии
          </Button>
          {requestNotice && <DecisionNote style={{ marginTop: 0 }}>{requestNotice}</DecisionNote>}
        </CrossModuleBar>
      )}
    </AnomalyCard>
  );
}

export function AnomaliesPanel({
  anomalies,
  runId,
  objectScope,
}: {
  anomalies: EconAnomaly[];
  runId: string;
  objectScope?: string | null;
}) {
  if (anomalies.length === 0) {
    return <Empty>Аномалий не выявлено — все показатели в пределах допустимых отклонений.</Empty>;
  }
  // Критичные неразмеченные — наверх: их ревьюеру важно увидеть первыми.
  const sorted = [...anomalies].sort((a, b) => {
    const score = (x: EconAnomaly) => (x.decision ? 0 : 2) + (x.severity === 'critical' ? 1 : 0);
    return score(b) - score(a);
  });
  return (
    <Stack>
      {sorted.map((a) => (
        <AnomalyItem key={a.id} anomaly={a} runId={runId} objectScope={objectScope} />
      ))}
    </Stack>
  );
}
